import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { ChevronLeft, Check, Clock, Sparkles } from "lucide-react";
import { getLesson, categoryLabel } from "../data/lessons";
import { isLessonComplete, setLessonComplete } from "../lib/localProgress";
import { lightTap, successPulse } from "../lib/haptics";

export default function LessonDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const lesson = getLesson(slug);
  const [done, setDone] = useState(false);

  useEffect(() => { if (lesson) setDone(isLessonComplete(lesson.slug)); }, [lesson]);

  if (!lesson) {
    return (
      <AppShell>
        <div className="px-8 pt-16 pb-10">
          <button onClick={() => navigate("/lessons")} className="text-brand text-sm">
            ← {t("v11.back")}
          </button>
          <p className="mt-4 text-foreground/70">{t("library.not_found")}</p>
        </div>
      </AppShell>
    );
  }

  const sections = lang === "es" ? lesson.sections_es : lesson.sections_en;
  const title = lang === "es" ? lesson.title_es : lesson.title_en;
  const reflection = lang === "es" ? lesson.reflection_es : lesson.reflection_en;

  const toggleDone = () => {
    const next = !done;
    setLessonComplete(lesson.slug, next);
    setDone(next);
    // Slightly stronger pulse only when *completing* a whole lesson —
    // never when un-marking it. One pulse per action.
    if (next) successPulse();
  };

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid={`page-lesson-${lesson.slug}`}>
        <button onClick={() => navigate("/lessons")} className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-2">
          <ChevronLeft className="w-4 h-4" /> {t("v11.back")}
        </button>
        <p className="font-script text-2xl text-brand">{categoryLabel(lesson.category, lang)}</p>
        <h1 className="font-serif text-4xl leading-tight">{title}</h1>
        <div className="mt-3 flex items-center gap-3 text-xs text-foreground/55">
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {t("v11.lessons.minutes", { n: lesson.read_minutes })}
          </span>
          {done && (
            <span className="inline-flex items-center gap-1 text-brand">
              <Check className="w-3 h-3" /> {t("v11.lessons.completed")}
            </span>
          )}
        </div>
      </div>

      <div className="px-8 space-y-5 pb-6">
        {sections.map((s, i) => (
          <section key={i} className="rounded-3xl bg-card border border-border p-5">
            {s.heading && (
              <p className="font-serif italic text-foreground/90 text-lg mb-2">{s.heading}</p>
            )}
            {s.body && <p className="text-foreground/85 leading-relaxed">{s.body}</p>}
            {Array.isArray(s.list) && (
              <ul className="mt-3 space-y-1.5">
                {s.list.map((it, j) => (
                  <li key={j} className="flex gap-2 text-foreground/85 leading-relaxed">
                    <span className="text-brand">·</span><span>{it}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}

        {reflection && (
          <section className="rounded-3xl bg-brand/8 border border-brand/20 p-6" data-testid="lesson-reflection">
            <p className="text-xs uppercase tracking-widest text-brand-deep flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> {t("v11.lessons.reflection")}
            </p>
            <p className="mt-2 font-serif italic text-foreground/90 text-lg leading-snug">
              {reflection}
            </p>
          </section>
        )}
      </div>

      <div className="px-8 pb-6">
        <Button
          data-testid="lesson-toggle-complete"
          onClick={toggleDone}
          className={`w-full h-13 rounded-full ${
            done
              ? "bg-card border border-brand text-brand hover:bg-brand/10"
              : "bg-brand hover:bg-brand/90 text-white"
          }`}
        >
          <Check className="w-4 h-4 mr-2" />
          {done ? t("v11.lessons.mark_incomplete") : t("v11.lessons.mark_complete")}
        </Button>
      </div>

      {Array.isArray(lesson.related) && lesson.related.length > 0 && (
        <div className="px-8 pb-10">
          <p className="text-xs uppercase tracking-widest text-foreground/55">{t("v11.lessons.related")}</p>
          <div className="mt-2 space-y-2">
            {lesson.related.map((rs) => {
              const r = getLesson(rs);
              if (!r) return null;
              return (
                <Link
                  key={rs}
                  to={`/lessons/${rs}`}
                  data-testid={`lesson-related-${rs}`}
                  className="block rounded-2xl bg-card border border-border p-4 hover:border-brand/50"
                >
                  <div className="font-medium">{lang === "es" ? r.title_es : r.title_en}</div>
                  <div className="text-xs text-foreground/55 mt-0.5">{categoryLabel(r.category, lang)}</div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </AppShell>
  );
}
